import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firestore } from '../firebase';
import { doc, setDoc, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import { useSimpleRateLimit } from '../utils/useSimpleRateLimit';
import { fetchBooks } from '../utils/bookService';
import Navigation from './Navigation';
import '../css/BookRecommendation.css';
import { rateLimiter } from '../utils/simpleRateLimiter';

// Helper function to process book results with improved error handling
const processBookResults = (results) => {
    if (!Array.isArray(results)) {
        return [];
    }

    return results
        .filter(book => {
            try {
                return book?.volumeInfo?.title &&
                    book?.volumeInfo?.authors?.length > 0 &&
                    book?.volumeInfo?.imageLinks?.thumbnail;
            } catch {
                return false;
            }
        })
        .map(book => {
            try {
                return {
                    id: book.id,
                    title: book.volumeInfo.title,
                    author: book.volumeInfo.authors[0],
                    genre: book.volumeInfo.categories?.[0] || 'General',
                    coverUrl: book.volumeInfo.imageLinks.thumbnail,
                    rating: book.volumeInfo.averageRating || 0,
                    ratingsCount: book.volumeInfo.ratingsCount || 0,
                    description: book.volumeInfo.description || 'No description available.'
                };
            } catch {
                return null;
            }
        })
        .filter(Boolean); // Remove any null entries from failed mappings
};

const BookRecommendation = () => {
    const navigate = useNavigate();
    const [currentBook, setCurrentBook] = useState(null);
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState({
        initial: true,
        refreshing: false,
        error: null
    });
    const [swipeDirection, setSwipeDirection] = useState(null);
    const [showOverlay, setShowOverlay] = useState(false);
    const [selectedBook, setSelectedBook] = useState(null);
    const [bookDescription, setBookDescription] = useState('');
    const [user, setUser] = useState(() => getAuth().currentUser);
    const [existingWantToRead, setExistingWantToRead] = useState(new Set());
    const [dislikedAuthors, setDislikedAuthors] = useState(new Set());
    const [isOnboarding, setIsOnboarding] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [onboardingBooks, setOnboardingBooks] = useState([]);
    const MIN_BOOKS_FOR_PERSONALIZATION = 5;
    const searchTimeoutRef = useRef(null);
    const MIN_RECOMMENDATIONS_POOL = 10;
    const TARGET_RECOMMENDATIONS_POOL = 20;
    const isFetchingRef = useRef(false);
    const lastFetchTimeRef = useRef(0);
    const MIN_FETCH_INTERVAL = 5000; // 5 seconds between fetches
    const [seenBooks, setSeenBooks] = useState(new Set());
    const [seenAuthors, setSeenAuthors] = useState(new Set());
    const seenBooksRef = useRef(new Set());
    const seenAuthorsRef = useRef(new Set());
    const [imgError, setImgError] = useState(false);
    const [overlayImgError, setOverlayImgError] = useState(false);

    // Initialize rate limiter with existing configuration
    const { checkRateLimit, getRemainingRequests } = useSimpleRateLimit({
        maxRequests: 10,
        timeWindow: 3600000, // 1 hour
        userSpecific: true
    });

    // Update refs when state changes
    useEffect(() => {
        seenBooksRef.current = seenBooks;
        seenAuthorsRef.current = seenAuthors;
    }, [seenBooks, seenAuthors]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(getAuth(), setUser);
        return () => unsubscribe();
    }, []);

    const handleSearch = useCallback(async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        // Check rate limit before proceeding
        if (!checkRateLimit(user?.uid)) {
            showErrorToast(`Please wait before searching again. ${getRemainingRequests(user?.uid)} requests remaining.`);
            return;
        }

        setSearchLoading(true);

        // Clear previous timeout before setting a new one
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const results = await fetchBooks(`q=${encodeURIComponent(query)}&maxResults=5`);
                const processedResults = processBookResults(results);
                setSearchResults(processedResults);
            } catch (error) {
                showErrorToast("Failed to search for books. Please try again.");
                setSearchResults([]);
            } finally {
                setSearchLoading(false);
            }
        }, 500); // 500ms debounce delay
    }, [checkRateLimit, getRemainingRequests, user?.uid]);

    const getPersonalizedRecommendations = useCallback(async (userBooks) => {
        try {
            if (!userBooks || userBooks.length === 0) {
                return [];
            }

            // Track genres, authors, and ratings
            const genreCounts = {};
            const authorCounts = {};
            const ratings = [];

            userBooks.forEach(book => {
                const genre = book.Genre || 'General';
                genreCounts[genre] = (genreCounts[genre] || 0) + 1;

                const author = book.Author || 'Unknown';
                authorCounts[author] = (authorCounts[author] || 0) + 1;

                if (book.Rating) {
                    ratings.push(book.Rating);
                }
            });

            const topGenres = Object.keys(genreCounts).slice(0, 2);
            const topAuthors = Object.keys(authorCounts)
                .filter(author => !dislikedAuthors.has(author))
                .slice(0, 3);

            const avgRating = ratings.length > 0 ? 
                ratings.reduce((a, b) => a + b) / ratings.length : 
                4.0;

            // Build query terms
            const authorTerms = topAuthors;
            const genreTerms = topGenres;

            const explorationGenres = [
                'Fantasy', 'Science Fiction', 'Mystery', 'Thriller',
                'Biography', 'Adventure', 'Nonfiction', 'Historical Fiction', 'Contemporary Fiction'
            ];

            const shuffledGenres = explorationGenres
                .filter(genre => !topGenres.includes(genre))
                .sort(() => Math.random() - 0.5)
                .slice(0, 1);

            if (shuffledGenres.length > 0) {
                genreTerms.push(shuffledGenres[0]);
            }

            // Try different search strategies
            let results = [];
            const searchStrategies = [
                () => `q=${encodeURIComponent(authorTerms.join(' OR '))}`,
                () => `q=${encodeURIComponent(genreTerms.join(' OR '))}`,
                () => `q=${encodeURIComponent(`${authorTerms.join(' OR ')} ${genreTerms.join(' OR ')}`)}`,
                () => 'q=Fiction'
            ];

            // Try each strategy until we get results
            for (const strategy of searchStrategies) {
                const searchQuery = strategy() + `&maxResults=40&startIndex=${Math.floor(Math.random() * 20)}&orderBy=relevance&printType=books&langRestrict=en`;
                results = await fetchBooks(searchQuery);
                
                if (results && results.length > 0) {
                    break;
                }
            }

            if (results && results.length > 0) {
                const userAuthors = new Set(userBooks.map(book => book.Author));
                const currentYear = new Date().getFullYear();

                const filteredResults = results
                    .filter(book => {
                        try {
                            const info = book.volumeInfo;
                            if (!info) return false;

                            const pubYear = parseInt(info.publishedDate?.slice(0, 4)) || 0;
                            const hasValidAuthors = Array.isArray(info.authors) && info.authors.length > 0;
                            const hasValidTitle = typeof info.title === 'string' && info.title.length > 0;

                            // Basic validation
                            if (!hasValidTitle || !hasValidAuthors) return false;

                            // Skip if we've seen this book before
                            if (seenBooksRef.current.has(book.id)) return false;

                            // Skip if we've seen this author before
                            if (seenAuthorsRef.current.has(info.authors[0])) return false;

                            // Skip if it's a book we already have
                            if (userAuthors.has(info.authors[0])) return false;

                            // Skip if it's from a disliked author
                            if (dislikedAuthors.has(info.authors[0])) return false;

                            // Skip if it's too old (more than 50 years)
                            if (pubYear < currentYear - 50) return false;

                            // If we have a rating, it should be decent
                            if (info.averageRating && info.averageRating < avgRating - 2) return false;

                            // If we have ratings count, it should have some reviews
                            if (info.ratingsCount && info.ratingsCount < 10) return false;

                            return true;
                        } catch (error) {
                            return false;
                        }
                    })
                    .sort(() => Math.random() - 0.5)
                    .slice(0, TARGET_RECOMMENDATIONS_POOL);

                return filteredResults.map(book => ({
                    id: book.id,
                    title: book.volumeInfo.title,
                    author: book.volumeInfo.authors[0],
                    genre: book.volumeInfo.categories?.[0] || 'General',
                    coverUrl: book.volumeInfo.imageLinks?.thumbnail || book.volumeInfo.imageLinks?.smallThumbnail,
                    rating: book.volumeInfo.averageRating || 0,
                    ratingsCount: book.volumeInfo.ratingsCount || 0
                }));
            }

            return [];
        } catch (error) {
            return [];
        }
    }, [dislikedAuthors]);

    const fetchRecommendations = useCallback(async () => {
        // Prevent multiple simultaneous fetches
        if (isFetchingRef.current) {
            return;
        }

        // Check if enough time has passed since last fetch
        const now = Date.now();
        if (now - lastFetchTimeRef.current < MIN_FETCH_INTERVAL) {
            return;
        }

        const user = getAuth().currentUser;
        if (!user) {
            setLoading(prev => ({ ...prev, error: "Please log in to get personalized recommendations" }));
            return;
        }

        if (!checkRateLimit(user.uid)) {
            setLoading(prev => ({ ...prev, error: `Please wait before refreshing recommendations. ${getRemainingRequests(user.uid)} requests remaining.` }));
            return;
        }

        try {
            isFetchingRef.current = true;
            setLoading(prev => ({ ...prev, refreshing: true, error: null }));
            
            const finishedBooksQuery = query(
                collection(firestore, "FinishedBook"),
                where("User_ID", "==", user.uid)
            );
            const finishedBooksSnapshot = await getDocs(finishedBooksQuery);
            const userBooks = finishedBooksSnapshot.docs.map(doc => doc.data());

            if (userBooks.length < MIN_BOOKS_FOR_PERSONALIZATION) {
                setIsOnboarding(true);
                return;
            }

            let recommendedBooks = await getPersonalizedRecommendations(userBooks);

            if (recommendedBooks.length === 0) {
                setLoading(prev => ({ 
                    ...prev, 
                    error: "We've run out of recommendations for now. Please try again later or add more books to your reading history for better recommendations." 
                }));
                return;
            }

            // Append new recommendations to existing ones
            setBooks(prevBooks => {
                const existingIds = new Set(prevBooks.map(book => book.id));
                const newBooks = recommendedBooks.filter(book => !existingIds.has(book.id));
                return [...prevBooks, ...newBooks];
            });

            // Only set current book if we don't have one
            if (!currentBook) {
                setCurrentBook(recommendedBooks[0] || null);
            }

            lastFetchTimeRef.current = now;
        } catch (error) {
            setLoading(prev => ({ ...prev, error: "Failed to fetch recommendations" }));
        } finally {
            isFetchingRef.current = false;
            setLoading(prev => ({ ...prev, initial: false, refreshing: false }));
        }
    }, [checkRateLimit, getRemainingRequests, getPersonalizedRecommendations, currentBook]);
    
    const handleAddOnboardingBook = useCallback(async (book) => {
        try {
            if (!user) {
                showErrorToast("Please log in to add books");
                return;
            }

            if (!checkRateLimit(user.uid)) {
                showErrorToast(`Please wait before adding another book. ${getRemainingRequests(user.uid)} requests remaining.`);
                return;
            }

            // Check if book already exists for this user
            const existingBookQuery = query(
                collection(firestore, "FinishedBook"),
                where("User_ID", "==", user.uid),
                where("Book_ID", "==", book.id)
            );
            const existingBookSnapshot = await getDocs(existingBookQuery);
            
            if (!existingBookSnapshot.empty) {
                showErrorToast("You've already added this book");
                return;
            }

            // Add the book to FinishedBook collection
            const bookRef = doc(collection(firestore, "FinishedBook"));
            await setDoc(bookRef, {
                User_ID: user.uid,
                Book_ID: book.id,
                Book_Title: book.title,
                Author: book.author,
                Cover_URL: book.coverUrl,
                Finished_date: new Date(),
                Rating: null
            });

            setOnboardingBooks(prev => [...prev, book]);
            showSuccessToast("Book added successfully!");

            if (onboardingBooks.length + 1 >= MIN_BOOKS_FOR_PERSONALIZATION) {
                setIsOnboarding(false);
                fetchRecommendations();
            }
        } catch (error) {
            showErrorToast("Failed to add book.");
        }
    }, [user, checkRateLimit, getRemainingRequests, onboardingBooks.length, fetchRecommendations]);

    // Load existing want to read books with improved error handling
    useEffect(() => {
        if (!rateLimiter.canMakeRequest(user?.uid)) {
            showErrorToast("Too many recommendation updates. Please wait a moment before trying again.");
            return;
        }
        fetchRecommendations();
    }, [fetchRecommendations, user?.uid]);

    useEffect(() => {
        if (!rateLimiter.canMakeRequest(user?.uid)) {
            showErrorToast("Too many recommendation updates. Please wait a moment before trying again.");
            return;
        }
        const loadExistingWantToRead = async () => {
            const user = getAuth().currentUser;
            if (!user) return;

            try {
                const wantToReadQuery = query(
                    collection(firestore, "wantToRead"),
                    where("userId", "==", user.uid)
                );
                const snapshot = await getDocs(wantToReadQuery);
                const bookIds = new Set(snapshot.docs.map(doc => doc.data().bookId));
                setExistingWantToRead(bookIds);
            } catch {
                showErrorToast("Failed to load your want to read list");
            }
        };

        loadExistingWantToRead();
    }, [user?.uid]);

    // Memoize the handleAddToWantToRead function
    const handleAddToWantToRead = useCallback(async (book) => {
        const user = getAuth().currentUser;
        if (!user) {
            showErrorToast("Please log in to add books to your want to read list");
            return;
        }

        if (!checkRateLimit(user.uid)) {
            showErrorToast(`Please wait before adding another book. ${getRemainingRequests(user.uid)} requests remaining.`);
            return;
        }

        if (existingWantToRead.has(book.id)) {
            showErrorToast("This book is already in your want to read list");
            return;
        }

        try {
            const wantToReadRef = doc(collection(firestore, "wantToRead"));
            await setDoc(wantToReadRef, {
                userId: user.uid,
                bookId: book.id,
                title: book.title,
                author: book.author,
                coverUrl: book.coverUrl,
                addedDate: new Date()
            });

            setExistingWantToRead(prev => new Set([...prev, book.id]));
            showSuccessToast("Book added to want to read list!");
        } catch {
            showErrorToast("Failed to add book to want to read list");
        }
    }, [existingWantToRead, checkRateLimit, getRemainingRequests]);

    const handleSwipe = useCallback(async (liked) => {
        if (!currentBook) return;

        setSwipeDirection(liked ? 'right' : 'left');

        try {
            if (liked) {
                await handleAddToWantToRead(currentBook);
            } else {
                setDislikedAuthors(prev => new Set([...prev, currentBook.author]));
            }

            // Add to seen books and authors
            setSeenBooks(prev => new Set([...prev, currentBook.id]));
            setSeenAuthors(prev => new Set([...prev, currentBook.author]));

            // Remove the current book from the pool
            setBooks(prevBooks => prevBooks.filter(book => book.id !== currentBook.id));

            // If we're running low on recommendations, fetch more
            if (books.length <= MIN_RECOMMENDATIONS_POOL) {
                fetchRecommendations();
            }

            // Set the next book from our pool
            const nextBook = books[1] || null;
            setCurrentBook(nextBook);
            
            setTimeout(() => {
                setSwipeDirection(null);
            }, 300);

        } catch (error) {
            showErrorToast("Failed to process your action");
        }
    }, [currentBook, books, fetchRecommendations, handleAddToWantToRead]);

    const handleBookClick = useCallback(async (book) => {
        setSelectedBook(book);
        setShowOverlay(true);
        try {
            // Check rate limit first
            if (!rateLimiter.canMakeRequest(user?.uid)) {
                showErrorToast("Too many requests. Please wait a moment before trying again.");
                return;
            }

            // First try to get from Firestore
            const bookRef = doc(firestore, "Book", book.id);
            const bookDoc = await getDoc(bookRef);
            
            if (bookDoc.exists()) {
                const firestoreBook = bookDoc.data();
                setBookDescription(firestoreBook.description || 'No description available.');
            } else {
                // If not in Firestore, try Google Books API
                const results = await fetchBooks(`q=${encodeURIComponent(book.title)}`);
                if (results && results.length > 0) {
                    const googleBook = results[0];
                    setBookDescription(googleBook.volumeInfo?.description || 'No description available.');
                } else {
                    setBookDescription('No description available.');
                }
            }
        } catch (error) {
            showErrorToast("Failed to load book description");
            setBookDescription('No description available.');
        }
    }, [user?.uid]);

    const handleCloseOverlay = useCallback(() => {
        setShowOverlay(false);
        setSelectedBook(null);
        setBookDescription('');
    }, []);

    if (loading.initial) {
        return <div className="book-recommendation loading">Loading recommendations...</div>;
    }

    if (loading.error) {
        return (
            <div className="book-recommendation error">
                <button 
                    className="recommendation-back-button"
                    onClick={() => navigate('/Bingo')}
                >
                    ← Back to Bingo
                </button>
                <h2>No More Recommendations</h2>
                <p>{loading.error}</p>
                <div className="error-actions">
                    <button 
                        onClick={() => {
                            setLoading(prev => ({ ...prev, error: null }));
                            fetchRecommendations();
                        }}
                        className="refresh-button"
                    >
                        Refresh Recommendations
                    </button>
                </div>
            </div>
        );
    }

    if (isOnboarding) {
        return (
            <div className="book-recommendation onboarding">
                <div className="onboarding-content">
                    <button 
                        className="recommendation-back-button"
                        onClick={() => navigate('/Bingo')}
                    >
                        ← Back to Bingo
                    </button>
                    <h2>Welcome to Book Recommendations!</h2>
                    <p>To get personalized book recommendations, please add at least {MIN_BOOKS_FOR_PERSONALIZATION} of your favorite books.</p>
                    <p>Books added: {onboardingBooks.length}/{MIN_BOOKS_FOR_PERSONALIZATION}</p>
                    <div className="search-section">
                        <input
                            type="text"
                            placeholder="Search for your favorite books..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                clearTimeout(window.searchTimeoutId);
                                window.searchTimeoutId = setTimeout(() => {
                                    handleSearch(e.target.value);
                                }, 500);
                            }}
                            className="search-input"
                        />
                        {searchLoading && <div className="search-loading">Searching...</div>}
                    </div>
                    <div className="search-results">
                        {searchResults.map(book => (
                            <div key={book.id} className="search-result-card">
                                <div className="book-result-content">
                                    <img 
                                        src={book.coverUrl} 
                                        alt={book.title}
                                        className="book-cover"
                                        onError={(e) => {
                                            e.target.src = 'placeholder-image.jpg';
                                        }}
                                    />
                                    <div className="book-info">
                                        <h3>{book.title}</h3>
                                        <p>{book.author}</p>
                                        <p className="genre">{book.genre}</p>
                                        <button 
                                            onClick={() => handleAddOnboardingBook(book)}
                                            disabled={onboardingBooks.some(b => b.id === book.id)}
                                            className="add-book-button"
                                        >
                                            {onboardingBooks.some(b => b.id === book.id) ? 'Added' : 'Add Book'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {onboardingBooks.length > 0 && (
                        <div className="added-books">
                            <h3>Your Favorite Books:</h3>
                            <div className="added-books-grid">
                                {onboardingBooks.map(book => (
                                    <div key={book.id} className="added-book-card">
                                        <img 
                                            src={book.coverUrl} 
                                            alt={book.title}
                                            className="book-cover"
                                        />
                                        <p>{book.title}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (!currentBook) {
        return (
            <div className="book-recommendation empty">
                <h2>No more books to recommend!</h2>
                <button 
                    onClick={fetchRecommendations}
                    disabled={loading.refreshing}
                >
                    {loading.refreshing ? 'Refreshing...' : 'Refresh Recommendations'}
                </button>
            </div>
        );
    }

    return (
        <div className="recommendations-page">
            <Navigation />
            <div className="book-recommendation">
                <div 
                    className={`book-card ${swipeDirection ? `swipe-${swipeDirection}` : ''}`}
                    onClick={() => handleBookClick(currentBook)}
                    style={{ cursor: 'pointer' }}
                >
                    <img 
                        src={!imgError && currentBook?.coverUrl ? currentBook.coverUrl : '/placeholder-image.jpg'} 
                        alt={currentBook?.title}
                        className="book-cover"
                        onError={() => setImgError(true)}
                    />
                    <div className="book-info">
                        <h2>{currentBook?.title}</h2>
                        <h3>{currentBook?.author}</h3>
                        <p className="genre">{currentBook?.genre}</p>
                        {currentBook?.rating > 0 && (
                            <p className="rating">
                                ⭐ {currentBook?.rating.toFixed(1)} ({currentBook?.ratingsCount || 0} ratings)
                            </p>
                        )}
                    </div>
                </div>

                <div className="swipe-buttons">
                    <button 
                        className="swipe-button dislike"
                        onClick={() => handleSwipe(false)}
                        disabled={loading.refreshing}
                    >
                        ✕
                    </button>
                    <button 
                        className="swipe-button like"
                        onClick={() => handleSwipe(true)}
                        disabled={loading.refreshing}
                    >
                        ♥
                    </button>
                </div>

                {showOverlay && selectedBook && (
                    <div className="book-overlay" onClick={handleCloseOverlay}>
                        <div className="book-overlay-content" onClick={e => e.stopPropagation()}>
                            <button 
                                className="close-overlay" 
                                onClick={handleCloseOverlay}
                                aria-label="Close overlay"
                            >
                                ×
                            </button>
                            <div className="overlay-book-header">
                                <img 
                                    src={!overlayImgError && selectedBook?.coverUrl ? selectedBook.coverUrl : '/placeholder-image.jpg'} 
                                    alt={selectedBook?.title}
                                    className="overlay-book-cover"
                                    onError={() => setOverlayImgError(true)}
                                />
                                <div className="overlay-book-info">
                                    <h2>{selectedBook?.title}</h2>
                                    <h3>{selectedBook?.author}</h3>
                                    <p className="genre">{selectedBook?.genre}</p>
                                    {selectedBook?.rating > 0 && (
                                        <p className="rating">
                                            ⭐ {selectedBook?.rating.toFixed(1)} ({selectedBook?.ratingsCount || 0} ratings)
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="overlay-book-description">
                                <h3>Description</h3>
                                <p>{bookDescription}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookRecommendation; 